import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../config/api'; // Make sure this path is correct
import { normalizeAttendanceResponse } from '../utils/attendance';
import { getTokenUserId } from '../utils/jwt';

function normalizeId(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

function assertAttendanceUserMatchesSession(args: {
  requestedUserId?: string | null;
  token?: string | null;
  selfUserId?: string | null;
  selfRole?: string | null;
}) {
  const { requestedUserId, token, selfUserId, selfRole } = args;
  if (!requestedUserId) return;
  if (selfRole === 'SUPER') return;

  const requested = normalizeId(requestedUserId);
  const self = normalizeId(selfUserId);
  const tokenUser = normalizeId(getTokenUserId(token));

  // Prefer comparing against the /self user id (server-derived).
  if (self && requested && self !== requested) {
    throw new Error(`Attendance request userId does not match logged-in user (self=${selfUserId}, requested=${requestedUserId}).`);
  }

  // If token contains a user id claim, ensure that matches too.
  if (tokenUser && requested && tokenUser !== requested) {
    throw new Error(`Attendance request userId does not match token user (token=${getTokenUserId(token)}, requested=${requestedUserId}).`);
  }
}


// --- TYPE DEFINITIONS ---

interface ErrorType {
  message: string;
}

// Define the shape of a single attendance item
interface Attendance {
  id: string;
  date: string;
  checkInTime: string;
  checkOutTime?: string;
  isBreak: boolean;
  userId: string;
  userName?: string;
  status?: number;
  latitudeIn?: number;
  longitudeIn?: number;
  latitudeOut?: number;
  longitudeOut?: number;
  fileIn?: string;
  fileOut?: string;
}

// --- ASYNC THUNKS (SIMPLIFIED) ---

// The interceptor now adds the token, so we just make the call.
export const fetchAttendance = createAsyncThunk<any>(
  'users/fetchAttendance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/attendances?pageSize=1000&sortOrder=DESC');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchAttendanceForUser = createAsyncThunk<any, { userId: string; startDate?: string; endDate?: string }>(
  'users/fetchAttendanceForUser',
  async ({ userId, startDate, endDate }, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      assertAttendanceUserMatchesSession({
        requestedUserId: userId,
        token: state?.auth?.token || localStorage.getItem('token'),
        selfUserId: state?.auth?.self?.id,
        selfRole: state?.auth?.self?.role,
      });

      const safeUserId = encodeURIComponent(userId);

      const resolvedEndDate = endDate || new Date().toISOString();
      const resolvedStartDate =
        startDate ||
        (() => {
          const start = new Date();
          start.setDate(start.getDate() - 365);
          return start.toISOString();
        })();

      const response = await api.get(
        `/attendances?userId=${safeUserId}&startDate=${encodeURIComponent(
          resolvedStartDate
        )}&endDate=${encodeURIComponent(resolvedEndDate)}&sortOrder=DESC&pageSize=1000`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error?.message || 'Request failed' });
    }
  }
);

interface CreateAttendancePayload {
  date: string;
  checkInTime: string;
  IsBreak: boolean;
  // Optional clock-in location + photo captured from the browser.
  LatitudeIn?: number;
  LongitudeIn?: number;
  FileIn?: string;
}

export const createAttendance = createAsyncThunk<Attendance, CreateAttendancePayload>(
  'users/createAttendance',
  async (attendanceData, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      assertAttendanceUserMatchesSession({
        requestedUserId: (attendanceData as any)?.UserId,
        token: state?.auth?.token || localStorage.getItem('token'),
        selfUserId: state?.auth?.self?.id,
        selfRole: state?.auth?.self?.role,
      });

      const response = await api.post('/attendances', attendanceData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error?.message || 'Request failed' });
    }
  }
);

interface UpdateAttendancePayload {
  // URL id
  id: string;

  // Optional client-only field used for validation; stripped before sending to API.
  requestedUserId?: string;

  // API payload (Swagger uses camelCase; older docs show PascalCase).
  date?: string;
  checkInTime?: string;
  checkOutTime?: string | null;
  isBreak?: boolean;
  status?: number;

  // Geolocation + photo captured at clock-in / clock-out.
  latitudeIn?: number;
  longitudeIn?: number;
  latitudeOut?: number;
  longitudeOut?: number;
  fileIn?: string;
  fileOut?: string;

  // Legacy/pascal aliases (accepted from callers; converted to camelCase).
  Date?: string;
  CheckInTime?: string;
  CheckOutTime?: string | null;
  IsBreak?: boolean;
  LatitudeIn?: number;
  LongitudeIn?: number;
  LatitudeOut?: number;
  LongitudeOut?: number;
  FileIn?: string;
  FileOut?: string;
  // Not part of swagger update body, but sometimes present in our code paths.
  UserId?: string;
}

export const updateAttendance = createAsyncThunk<Attendance, UpdateAttendancePayload>(
  'users/updateAttendance',
  async (attendanceData, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const selfUserId = state?.auth?.self?.id ? String(state.auth.self.id) : null;
      const selfRole = state?.auth?.self?.role || null;
      const requestedUserId =
        (attendanceData as any)?.requestedUserId || (attendanceData as any)?.UserId;
      assertAttendanceUserMatchesSession({
        requestedUserId,
        token: state?.auth?.token || localStorage.getItem('token'),
        selfUserId,
        selfRole,
      });

      const { id, ...rest } = attendanceData || ({} as UpdateAttendancePayload);

      const payload: Record<string, any> = { ...rest };
      delete payload.requestedUserId;

      const userIdForBody =
        requestedUserId || (selfRole === 'SUPER' ? null : selfUserId);

      // Normalize to swagger's camelCase body shape.
      const normalizedPayload: Record<string, any> = {
        id,
        ...(userIdForBody ? { userId: userIdForBody } : null),
        date: payload.date ?? payload.Date,
        checkInTime: payload.checkInTime ?? payload.CheckInTime,
        checkOutTime: payload.checkOutTime ?? payload.CheckOutTime,
        isBreak: payload.isBreak ?? payload.IsBreak,
      };

      if (payload.status !== undefined) {
        normalizedPayload.status = payload.status;
      }

      // Pass through any captured geolocation (camelCase or legacy PascalCase).
      const coordinateFields: Record<string, any> = {
        latitudeIn: payload.latitudeIn ?? payload.LatitudeIn,
        longitudeIn: payload.longitudeIn ?? payload.LongitudeIn,
        latitudeOut: payload.latitudeOut ?? payload.LatitudeOut,
        longitudeOut: payload.longitudeOut ?? payload.LongitudeOut,
      };

      Object.entries(coordinateFields).forEach(([key, value]) => {
        const numeric = Number(value);
        if (value !== undefined && value !== null && Number.isFinite(numeric)) {
          normalizedPayload[key] = numeric;
        }
      });

      // Pass through the clock-in / clock-out photos when present.
      const fileFields: Record<string, any> = {
        fileIn: payload.fileIn ?? payload.FileIn,
        fileOut: payload.fileOut ?? payload.FileOut,
      };

      Object.entries(fileFields).forEach(([key, value]) => {
        if (typeof value === 'string' && value.length) {
          normalizedPayload[key] = value;
        }
      });

      const response = await api.put(`/attendances/${id}`, normalizedPayload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error?.message || 'Request failed' });
    }
  }
);

interface SearchAttendancePayload {
  userId: string;
  startDate: string;
  endDate: string;
}

export const searchAttendance = createAsyncThunk<any, SearchAttendancePayload>(
  'users/searchAttendance',
  async (searchData, { rejectWithValue, getState }) => {
    try {
      const { userId, startDate, endDate } = searchData;
      const state: any = getState();
      assertAttendanceUserMatchesSession({
        requestedUserId: userId,
        token: state?.auth?.token || localStorage.getItem('token'),
        selfUserId: state?.auth?.self?.id,
        selfRole: state?.auth?.self?.role,
      });
      const response = await api.get(`/attendances?userId=${userId}&startDate=${startDate}&endDate=${endDate}&sortOrder=DESC&pageSize=1000`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error?.message || 'Request failed' });
    }
  }
);

// --- SLICE DEFINITION ---

interface AttendanceState {
  items: Attendance[];
  searchItems: Attendance[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Legacy
  listStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  saveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  searchStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  clockin: Attendance | null;
  clockout: Attendance | null;
  error: ErrorType | null;
}

const initialState: AttendanceState = {
  items: [],
  searchItems: [],
  status: 'idle',
  listStatus: 'idle',
  saveStatus: 'idle',
  searchStatus: 'idle',
  clockin: null,
  clockout: null,
  error: null,
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // All .addCase handlers now use the correct types, preventing future errors
      .addCase(fetchAttendance.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchAttendance.fulfilled, (state, action: PayloadAction<any>) => {
        state.listStatus = 'succeeded';
        state.items = normalizeAttendanceResponse(action.payload);
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(fetchAttendanceForUser.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchAttendanceForUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.listStatus = 'succeeded';
        state.items = normalizeAttendanceResponse(action.payload);
      })
      .addCase(fetchAttendanceForUser.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(createAttendance.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createAttendance.fulfilled, (state, action: PayloadAction<Attendance>) => {
        state.saveStatus = 'succeeded';
        state.clockin = action.payload;
      })
      .addCase(createAttendance.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(updateAttendance.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateAttendance.fulfilled, (state, action: PayloadAction<Attendance>) => {
        state.saveStatus = 'succeeded';
        state.clockout = action.payload;
      })
      .addCase(updateAttendance.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(searchAttendance.pending, (state) => {
        state.searchStatus = 'loading';
      })
      .addCase(searchAttendance.fulfilled, (state, action: PayloadAction<any>) => {
        state.searchStatus = 'succeeded';
        state.searchItems = normalizeAttendanceResponse(action.payload);
      })
      .addCase(searchAttendance.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.error = action.payload as ErrorType;
      });
  }
});

export default attendanceSlice.reducer;
